<template>
    <div class="admin-instance-page sip-view-page">
        <div class="nk-block-head nk-block-head-sm sip-view-head">
            <div class="nk-block-between">
                <div class="nk-block-head-content admin-instance-page__intro">
                    <router-link
                        to="/instance"
                        class="admin-instance-page__back admin-instance-page__back--pill"
                        :title="trans('instance.instance')"
                        :aria-label="trans('instance.instance')"
                    >
                        <admin-icon name="chevron-left" aria-hidden="true"></admin-icon>
                    </router-link>
                    <div class="admin-instance-page__intro-text">
                        <h3 class="nk-block-title page-title mb-0">{{ trans('instance.instance') }}</h3>
                        <div class="nk-block-des text-soft admin-instance-page__intro-meta" v-if="instance.domain || instance.email">
                            <span v-if="instance.domain" class="admin-instance-page__domain">{{ instance.domain }}</span>
                            <span v-if="instance.domain && instance.email" class="admin-instance-page__dot">·</span>
                            <span v-if="instance.email">{{ instance.email }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="row admin-instance-page__grid">
            <div class="col-12 col-xl-5">
                <div class="card sip-view-card admin-instance-hero-card">
                    <div class="card-body">
                        <div class="sip-view-status">
                            <span class="admin-status-badge" :class="'admin-status-badge--' + instanceStatusTone">{{ trans('instance.' + instanceStatusNormalized) }}</span>
                            <span v-if="instance.is_trial" class="admin-status-badge admin-status-badge--warning">{{ trans('subscription.trial') }}</span>
                            <span v-if="instanceStatusNormalized !== 'pending'" class="sip-view-status__meta">
                                {{ trans('subscription.till') }} {{ instance.date_of_expiry | moment }}
                            </span>
                        </div>

                        <dl class="sip-view-dl admin-instance-meta">
                            <div class="admin-instance-meta__row">
                                <dt>{{ trans('instance.email') }}</dt>
                                <dd>{{ instance.email }}</dd>
                            </div>
                            <div class="admin-instance-meta__row">
                                <dt>{{ trans('instance.domain') }}</dt>
                                <dd><code class="admin-instance-meta__code">{{ instance.domain }}</code></dd>
                            </div>
                            <div class="admin-instance-meta__row">
                                <dt>{{ trans('instance.custom_domain') }}</dt>
                                <dd>{{ instance.custom_domain || '—' }}</dd>
                            </div>
                            <div class="admin-instance-meta__row">
                                <dt>{{ trans('user.name') }}</dt>
                                <dd>{{ instance.first_name + ' ' + instance.last_name }}</dd>
                            </div>
                            <div class="admin-instance-meta__row">
                                <dt>{{ trans('plan.plan') }}</dt>
                                <dd><template v-if="instance.plan">{{ instance.plan.name }}</template><span class="admin-instance-meta__price">{{ getPrice }}</span></dd>
                            </div>
                            <div class="admin-instance-meta__row">
                                <dt>{{ trans('instance.created_at') }}</dt>
                                <dd>{{ instance.created_at | momentDateTime }}</dd>
                            </div>
                            <div class="admin-instance-meta__row admin-instance-meta__row--last">
                                <dt>{{ trans('instance.updated_at') }}</dt>
                                <dd>{{ instance.updated_at | momentDateTime }}</dd>
                            </div>
                        </dl>
                    </div>
                </div>

                <div
                    class="card sip-view-card admin-instance-form-card"
                    v-if="instanceStatusNormalized !== 'pending' && hasPermission('edit-instance')"
                >
                    <div class="card-body">
                        <div class="admin-instance-card-head">
                            <h4 class="admin-instance-card-head__title">{{ trans('user.force_change_password') }}</h4>
                        </div>
                        <form @submit.prevent="updatePassword" @keydown="updatePasswordForm.errors.clear($event.target.name)">
                            <div class="form-group">
                                <label>{{ trans('auth.new_password') }}</label>
                                <input class="form-control" type="password" name="new_password" v-model="updatePasswordForm.new_password">
                                <show-error :form-name="updatePasswordForm" prop-name="new_password"></show-error>
                            </div>
                            <div class="form-group">
                                <label>{{ trans('auth.new_password_confirmation') }}</label>
                                <input class="form-control" type="password" name="new_password_confirmation" v-model="updatePasswordForm.new_password_confirmation">
                                <show-error :form-name="updatePasswordForm" prop-name="new_password_confirmation"></show-error>
                            </div>
                            <div class="admin-instance-form-actions">
                                <button type="submit" class="btn btn-sm admin-toolbar-btn admin-toolbar-btn--solid">{{ trans('general.save') }}</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <div class="col-12 col-xl-7">
                <div class="card sip-view-card admin-instance-form-card" v-if="instanceStatusNormalized !== 'pending' && hasPermission('edit-instance')">
                    <div class="card-body">
                        <div class="admin-instance-card-head">
                            <h4 class="admin-instance-card-head__title">{{ trans('instance.update') }}</h4>
                        </div>
                        <form @submit.prevent="update" @keydown="updateInstanceForm.errors.clear($event.target.name)">
                            <div class="row">
                                <div class="col-12 col-md-6 form-group">
                                    <label>{{ trans('instance.status') }}</label>
                                    <select name="status" class="form-control" v-model="updateInstanceForm.status">
                                        <option value="running">{{ trans('instance.running') }}</option>
                                        <option value="expired">{{ trans('instance.expired') }}</option>
                                        <option value="suspended">{{ trans('instance.suspended') }}</option>
                                        <option value="terminated">{{ trans('instance.terminated') }}</option>
                                    </select>
                                    <show-error :form-name="updateInstanceForm" prop-name="status"></show-error>
                                </div>
                                <div class="col-12 col-md-6 form-group">
                                    <label>{{ trans('subscription.validity') }}</label>
                                    <datepicker v-model="updateInstanceForm.date_of_expiry" :bootstrapStyling="true" name="date_of_expiry" @selected="updateInstanceForm.errors.clear('date_of_expiry')"></datepicker>
                                    <show-error :form-name="updateInstanceForm" prop-name="date_of_expiry"></show-error>
                                </div>
                                <div class="col-12 col-md-6 form-group">
                                    <label>{{ trans('instance.custom_domain') }}</label>
                                    <input class="form-control" type="text" name="custom_domain" v-model="updateInstanceForm.custom_domain">
                                    <show-error :form-name="updateInstanceForm" prop-name="custom_domain"></show-error>
                                </div>
                                <div class="col-12 col-md-6 form-group">
                                    <label>{{ trans('instance.max_student') }}</label>
                                    <input class="form-control" type="text" name="max_student" v-model="updateInstanceForm.max_student">
                                    <show-error :form-name="updateInstanceForm" prop-name="max_student"></show-error>
                                </div>
                                <div class="col-12 form-group">
                                    <label>{{ trans('instance.remarks') }}</label>
                                    <autosize-textarea rows="2" name="remarks" :placeholder="trans('instance.remarks')" v-model="updateInstanceForm.remarks"></autosize-textarea>
                                    <show-error :form-name="updateInstanceForm" prop-name="remarks"></show-error>
                                </div>
                                <div class="col-12 form-group admin-instance-toggle-row">
                                    <label class="mb-0">{{ trans('configuration.single_instance_maintenance_mode') }}</label>
                                    <switches v-model="updateInstanceForm.maintenance_mode" name="maintenance_mode" theme="bootstrap" color="success"></switches>
                                </div>
                                <template v-if="updateInstanceForm.maintenance_mode">
                                    <div class="col-12 form-group">
                                        <label>{{ trans('configuration.instance_maintenance_mode_message') }}</label>
                                        <autosize-textarea rows="2" name="maintenance_mode_message" :placeholder="trans('configuration.instance_maintenance_mode_message')" v-model="updateInstanceForm.maintenance_mode_message"></autosize-textarea>
                                        <show-error :form-name="updateInstanceForm" prop-name="maintenance_mode_message"></show-error>
                                    </div>
                                    <div class="col-12 form-group">
                                        <label>{{ trans('configuration.instance_maintenance_exclude_ip') }} <show-tip module="configuration" tip="tip_instance_maintenance_exclude_ip" type="field"></show-tip></label>
                                        <autosize-textarea rows="2" name="exclude_ip_from_maintenance" :placeholder="trans('configuration.instance_maintenance_exclude_ip')" v-model="updateInstanceForm.exclude_ip_from_maintenance"></autosize-textarea>
                                        <show-error :form-name="updateInstanceForm" prop-name="exclude_ip_from_maintenance"></show-error>
                                    </div>
                                </template>
                            </div>
                            <div class="admin-instance-form-actions">
                                <button type="submit" class="btn btn-sm admin-toolbar-btn admin-toolbar-btn--solid">{{ trans('general.save') }}</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <div class="col-12 admin-instance-page__subscriptions">
                <div class="admin-instance-section-bar">
                    <div>
                        <h4 class="admin-instance-section-bar__title">{{ trans('subscription.subscription') }}</h4>
                        <p class="admin-instance-section-bar__meta" v-if="subscriptions.total">
                            {{ trans('general.total_result_found', { count: subscriptions.total }) }}
                        </p>
                    </div>
                </div>

                <div class="admin-list-table-card mb-4" v-if="subscriptions.total">
                    <div class="table-responsive">
                        <table class="table admin-data-table admin-data-table--config-list mb-0">
                            <thead>
                                <tr>
                                    <th>{{ trans('subscription.date') }}</th>
                                    <th>{{ trans('subscription.amount') }}</th>
                                    <th>{{ trans('subscription.plan') }}</th>
                                    <th>{{ trans('subscription.method') }}</th>
                                    <th>{{ trans('subscription.validity') }}</th>
                                    <th>{{ trans('subscription.status') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="subscription in subscriptions.data" :key="subscription.id">
                                    <td>{{ subscription.created_at | moment }}</td>
                                    <td v-text="formatCurrency(subscription.amount, subscription.currency)"></td>
                                    <td v-text="subscription.plan.name"></td>
                                    <td v-text="subscription.method === 'paypal' ? 'Paypal' : 'Credit Card'"></td>
                                    <td>{{ subscription.validity | moment }}</td>
                                    <td>
                                        <span v-if="subscription.status" class="admin-status-badge admin-status-badge--success">{{ trans('subscription.complete') }}</span>
                                        <span v-else class="admin-status-badge admin-status-badge--danger">{{ trans('subscription.failed') }}</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="admin-table-pagination-wrap">
                        <pagination-record :page-length.sync="filterSubscriptionForm.page_length" :records="subscriptions" @updateRecords="getSubscriptions" @change.native="getSubscriptions"></pagination-record>
                    </div>
                </div>

                <div class="admin-list-table-card admin-empty-state mb-4" v-if="!subscriptions.total" role="status">
                    <div class="admin-empty-state__inner">
                        <div class="admin-empty-state__icon-wrap" aria-hidden="true">
                            <admin-icon name="receipt"></admin-icon>
                        </div>
                        <h4 class="admin-empty-state__title">{{ trans('subscription.module_info_title') }}</h4>
                        <p class="admin-empty-state__text">{{ trans('subscription.module_info_description') }}</p>
                    </div>
                </div>

                <div class="card sip-view-card admin-instance-form-card admin-instance-form-card--subscription">
                    <div class="card-body">
                        <div class="admin-instance-card-head">
                            <h4 class="admin-instance-card-head__title">{{ trans('subscription.add_new') }}</h4>
                        </div>
                        <p class="sip-view-note">{{ trans('subscription.add_new_tip') }}</p>
                        <form @submit.prevent="addNewSubscription" @keydown="subscriptionForm.errors.clear($event.target.name)">
                            <div class="row">
                                <div class="col-12 col-md-6 form-group">
                                    <label>{{ trans('plan.plan') }}</label>
                                    <select name="plan_id" class="form-control" v-model="subscriptionForm.plan_id">
                                        <option value="">{{ trans('general.select_one') }}</option>
                                        <option :value="plan.id" v-for="plan in plans" :key="plan.id">{{ plan.name }}</option>
                                    </select>
                                    <show-error :form-name="subscriptionForm" prop-name="plan_id"></show-error>
                                </div>
                                <div class="col-12 col-md-6 form-group">
                                    <label>{{ trans('plan.payment_frequency') }}</label>
                                    <select name="frequency" class="form-control" v-model="subscriptionForm.frequency">
                                        <option value="">{{ trans('general.select_one') }}</option>
                                        <option :value="frequency.id" v-for="frequency in payment_frequencies" :key="frequency.id">{{ frequency.name }}</option>
                                    </select>
                                    <show-error :form-name="subscriptionForm" prop-name="frequency"></show-error>
                                </div>
                                <div class="col-12 col-md-6 form-group">
                                    <label>{{ trans('currency.currency') }}</label>
                                    <select name="currency_id" class="form-control" v-model="subscriptionForm.currency_id">
                                        <option value="">{{ trans('general.select_one') }}</option>
                                        <option :value="currency.id" v-for="currency in currencies" :key="currency.id">{{ currency.name }}</option>
                                    </select>
                                    <show-error :form-name="subscriptionForm" prop-name="currency_id"></show-error>
                                </div>
                                <div class="col-12 col-md-6 form-group">
                                    <label>{{ trans('subscription.amount') }}</label>
                                    <input class="form-control" type="text" name="amount" v-model="subscriptionForm.amount" :placeholder="trans('subscription.amount')">
                                    <show-error :form-name="subscriptionForm" prop-name="amount"></show-error>
                                </div>
                                <div class="col-12 col-md-6 form-group">
                                    <label>{{ trans('subscription.validity') }}</label>
                                    <datepicker v-model="subscriptionForm.date_of_expiry" :bootstrapStyling="true" name="date_of_expiry" @selected="subscriptionForm.errors.clear('date_of_expiry')"></datepicker>
                                    <show-error :form-name="subscriptionForm" prop-name="date_of_expiry"></show-error>
                                </div>
                                <div class="col-12 col-md-6 form-group">
                                    <label>{{ trans('subscription.payment_method') }}</label>
                                    <input class="form-control" type="text" name="payment_method" v-model="subscriptionForm.payment_method" :placeholder="trans('subscription.payment_method')">
                                    <show-error :form-name="subscriptionForm" prop-name="payment_method"></show-error>
                                </div>
                                <div class="col-12 col-md-6 form-group">
                                    <label>{{ trans('subscription.reference_number') }}</label>
                                    <input class="form-control" type="text" name="reference_number" v-model="subscriptionForm.reference_number" :placeholder="trans('subscription.reference_number')">
                                    <show-error :form-name="subscriptionForm" prop-name="reference_number"></show-error>
                                </div>
                                <div class="col-12 form-group">
                                    <label>{{ trans('instance.remarks') }}</label>
                                    <autosize-textarea rows="2" name="remarks" :placeholder="trans('instance.remarks')" v-model="subscriptionForm.remarks"></autosize-textarea>
                                    <show-error :form-name="subscriptionForm" prop-name="remarks"></show-error>
                                </div>
                            </div>
                            <div class="admin-instance-form-actions">
                                <button type="submit" class="btn btn-sm admin-toolbar-btn admin-toolbar-btn--solid">{{ trans('general.save') }}</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import autosizeTextarea from '../../components/autosize-textarea'
    import datepicker from 'vuejs-datepicker'
    import switches from 'vue-switches'

    export default {
        components:{autosizeTextarea,datepicker,switches},
        data() {
            return {
                id:this.$route.params.id,
                instance: {
                    status: 'pending',
                    email: '',
                    domain: '',
                    custom_domain: '',
                    first_name: '',
                    last_name: '',
                    plan: {
                        name: '',
                        plan_price: []
                    },
                    currency: {

                    }
                },
                subscriptions: {
                    total: 0,
                    data: []
                },
                filterSubscriptionForm: {
                    instance_id : '',
                    page_length: helper.getConfig('page_length')
                },
                updatePasswordForm: new Form({
                    new_password: '',
                    new_password_confirmation: ''
                }),
                updateInstanceForm : new Form({
                    status: '',
                    custom_domain: '',
                    max_student: '',
                    date_of_expiry: '',
                    remarks: '',
                    maintenance_mode: false,
                    maintenance_mode_message: '',
                    exclude_ip_from_maintenance: ''
                },false),
                payment_frequencies: [],
                currencies: [],
                plans: [],
                subscriptionForm: new Form({
                    plan_id: '',
                    date_of_expiry: '',
                    frequency: '',
                    currency_id: '',
                    amount: '',
                    payment_method: '',
                    reference_number: '',
                    remarks: ''
                })
            };
        },
        mounted(){
            if(!helper.hasPermission('list-instance')){
                helper.notAccessibleMsg();
                this.$router.push('/home');
            }

            this.getInstance();
        },
        methods: {
            getInstance(){
                axios.get('/api/instance/'+this.id)
                    .then(response => response.data)
                    .then(response => {
                        this.instance = response.instance;
                        this.filterSubscriptionForm.instance_id = this.instance.id;
                        this.updateInstanceForm.status = this.instance.status;
                        this.updateInstanceForm.custom_domain = this.instance.custom_domain;
                        this.updateInstanceForm.max_student = this.instance.options.max_student;
                        this.updateInstanceForm.date_of_expiry = this.instance.date_of_expiry;
                        this.updateInstanceForm.maintenance_mode = this.instance.maintenance_mode;
                        this.updateInstanceForm.maintenance_mode_message = this.instance.maintenance_mode_message;
                        this.updateInstanceForm.exclude_ip_from_maintenance = this.instance.exclude_ip_from_maintenance;
                        this.payment_frequencies = response.pre_requisites.payment_frequencies;
                        this.currencies = response.pre_requisites.currencies;
                        this.plans = response.pre_requisites.plans;
                        this.getSubscriptions();
                    })
                    .catch(error => {
                        helper.showDataErrorMsg(error);
                        this.$router.push('/instance');
                    });

            },
            getSubscriptions(page){
                if (typeof page !== 'number') {
                    page = 1;
                }
                let url = helper.getFilterURL(this.filterSubscriptionForm);
                axios.get('/api/subscription?page=' + page + url)
                    .then(response => response.data)
                    .then(response => this.subscriptions = response)
                    .catch(error => {
                        helper.showDataErrorMsg(error);
                    });
            },
            update(){
                this.updateInstanceForm.maintenance_mode = (this.updateInstanceForm.maintenance_mode) ? 1 : 0;
                this.updateInstanceForm.date_of_expiry = (this.updateInstanceForm.date_of_expiry) ? moment(this.updateInstanceForm.date_of_expiry).format('YYYY-MM-DD') : null;
                this.updateInstanceForm.patch('/api/instance/'+this.instance.id)
                    .then(response => {
                        toastr.success(response.message);
                        this.updateInstanceForm.remarks = '';
                        this.getInstance();
                    })
                    .catch(error => {
                        helper.showErrorMsg(error);
                    })
            },
            updatePassword(){
                this.updatePasswordForm.post('/api/instance/'+this.instance.id+'/password')
                    .then(response => {
                        toastr.success(response.message);
                        this.getInstance();
                    })
                    .catch(error => {
                        helper.showErrorMsg(error);
                    })
            },
            formatCurrency(amount, currency){
                return helper.formatCurrency(amount, currency);
            },
            hasPermission(permission){
                return helper.hasPermission(permission);
            },
            addNewSubscription() {
                this.subscriptionForm.post('/api/instance/'+this.instance.id+'/subscription')
                    .then(response => {
                        toastr.success(response.message);
                        this.getInstance();
                    })
                    .catch(error => {
                        helper.showErrorMsg(error);
                    })
            }
        },
        computed: {
            instanceStatusNormalized() {
                return String(this.instance.status || 'pending').toLowerCase();
            },
            instanceStatusTone() {
                const map = {
                    pending: 'info',
                    running: 'success',
                    expired: 'danger',
                    suspended: 'warning',
                    terminated: 'danger',
                };
                return map[this.instanceStatusNormalized] || 'info';
            },
            getPrice(){
                let price = this.instance.plan.plan_price.find(o => o.frequency === this.instance.frequency && o.currency_id == this.instance.currency.id);

                return (price) ? ('('+helper.formatCurrency(price.amount, this.instance.currency)+' / '+ (price.frequency === 'monthly' ? 'Mo' : 'Yr') +')') : '';
            }
        },
        filters: {
          moment(date) {
            return helper.formatDate(date);
          },
          momentDateTime(datetime) {
            return helper.formatDateTime(datetime);
          }
        }
    }
</script>
